import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import _ from "lodash";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FriendRow from "./FriendRow";
import RequestRow from "./RequestRow";

const useStyles = makeStyles((theme) => ({
    friendListSideBar: {
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1002,
    },
    header: {
        background: '#171a1d',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '0.35rem 0.4rem 0.25rem 0.4rem',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'none',
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.4rem 0.5rem 0.3rem 0.5rem',
        },
    },
    headerCloseButton: {
        position: 'absolute',
        top: 6,
        right: 6,
        cursor: 'pointer',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '1.1rem',
        height: '1.1rem',
        padding: '0.35rem',
        transition: 'background-color 0.3s ease',
        borderRadius: '50%',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
    },
    closeIcon: {
        fontSize: '1rem',
        color: '#ffffff',
        fontWeight: 700,
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.05rem',
        },
    },
    headerTitle: {
        color: '#ffffff',
        fontSize: '0.88rem',
        fontWeight: 700,
        marginBottom: '0.2rem',
        textAlign: 'left',
        textShadow: 'none',
        letterSpacing: '0.3px',
    },
    requestsSection: {
        marginBottom: '0.3rem',
    },
    requestsHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '0.15rem',
    },
    requestsTitle: {
        color: '#dce1e6',
        fontSize: '0.62rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.45px',
    },
    requestsCount: {
        color: '#E8A33D',
        fontSize: '0.62rem',
        fontWeight: 700,
    },
    showMoreRequestsButton: {
        marginTop: '0.25rem',
        fontSize: '0.68rem',
        color: '#c7ced6',
        padding: '0.2rem 0',
        textTransform: 'none',
        justifyContent: 'flex-start',
        minHeight: 'auto',
        '&:hover': {
            color: '#ffffff',
            background: 'transparent',
        },
    },
    content: {
        flex: 1,
        padding: '0.25rem',
        minHeight: 0,
        overflow: 'auto',
        '&::-webkit-scrollbar': {
            width: '4px',
        },
        '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#95989f',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: '#7a7e86',
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.3rem 0.3rem',
        },
    },
    contentSection: {
        display: 'block',
        animation: '$fadeIn 0.3s ease-in-out',
    },
    friendListContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    containerOnlineFriends: {
        display: 'flex',
        flexDirection: 'column',
    },
    containerOfflineFriends: {
        display: 'flex',
        flexDirection: 'column',
    },
    textOnlineFriends: {
        width: '100%',
        fontFamily: 'Roboto',
        fontSize: '0.62rem',
        color: '#8e949b',
        fontWeight: 700,
        marginBottom: '0.2rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.68rem',
        },
    },
    textOfflineFriends: {
        width: '100%',
        fontFamily: 'Roboto',
        fontSize: '0.62rem',
        color: '#8e949b',
        fontWeight: 700,
        margin: '0.3rem 0 0.2rem 0',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.68rem',
        },
    },
    searchContainer: {
        marginBottom: '0.3rem',
    },
    searchInput: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.035)',
            borderRadius: '5px',
            '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.14)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'rgba(232, 163, 61, 0.65)',
            },
        },
        '& .MuiOutlinedInput-input': {
            color: '#ffffff',
            fontSize: '0.72rem',
            padding: '0.24rem 0.45rem',
            [theme.breakpoints.up("xl")]: {
                fontSize: '0.76rem',
                padding: '0.26rem 0.48rem',
            },
        },
    },
    containerFriends: {
        display: 'flex',
        flexDirection: 'column',
    },
    requestListContainer: {
        overflow: 'hidden',
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0,
            transform: 'translateY(10px)',
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0)',
        }
    }
}));

export default function FriendListSideBar({ 
    setSidebarOpen, 
    friendRequests,
    handleAcceptFriendRequest,
    handleDeclineFriendRequest,
    handleFriendRequestToCancel,
    friends,
    handleDeleteFriend,
    handleSendInvite,
    inviteState,
    handleClearInviteError,
    actions,
    catalog,
    isLobbyFull = false,
}) {
    const classes = useStyles();
    const [friendSearchQuery, setFriendSearchQuery] = useState('');
    const [visibleRequestCount, setVisibleRequestCount] = useState(3);

    useEffect(() => {
        // Keep the visible window stable and valid as requests are accepted/declined.
        if (friendRequests.length <= 3) {
            setVisibleRequestCount(3);
            return;
        }
        if (visibleRequestCount > friendRequests.length) {
            setVisibleRequestCount(friendRequests.length);
        }
    }, [friendRequests.length, visibleRequestCount]);

    // Filter friends by search query
    const filteredFriends = friends.filter((friend) => {
        if (!friendSearchQuery.trim()) return true;
        const query = friendSearchQuery.toLowerCase();
        const friendNickname = friend.friendData?.nickname || friend.friendData?.name || '';
        return friendNickname.toLowerCase().includes(query);
    });

    const onlineFriends = filteredFriends.filter((friend) => friend.friendData?.isOnline);
    const onlineFriendsInYourTeamSorted = _.orderBy(
        onlineFriends,
        [(friend) => !!inviteState[friend.friendData?.userId]],
        ['desc']
    );
    const offlineFriends = filteredFriends.filter((friend) => !friend.friendData?.isOnline);
    const visibleRequests = friendRequests.slice(0, visibleRequestCount);
    const hasMoreRequests = friendRequests.length > visibleRequestCount;
    
    const isLeader = catalog?.user ? (catalog.currentLobby?.hostId === catalog.user.userId) : false;
    
    return (
        <div className={classes.friendListSideBar}>
            <div className={classes.header}>
                <div className={classes.headerCloseButton} onClick={() => setSidebarOpen(false)}>
                    <CloseIcon className={classes.closeIcon} />
                </div>
                <Typography className={classes.headerTitle}>
                    Friends
                </Typography>
            </div>

            <div className={classes.content}>
                <div className={classes.contentSection}>
                    
                    {/* Search Friends */}
                    <div className={classes.searchContainer}>
                        <TextField
                          id="friend-search-input"
                          fullWidth
                          placeholder="Search friends..."
                          value={friendSearchQuery}
                          onChange={(e) => setFriendSearchQuery(e.target.value)}
                          className={classes.searchInput}
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: '#999999', mr: 1 }} />,
                          }}
                        />
                    </div>

                    {friendRequests.length > 0 && (
                        <div className={classes.requestsSection}>
                            <div className={classes.requestsHeaderRow}>
                                <Typography className={classes.requestsTitle}>
                                    Friend Requests
                                </Typography>
                                <Typography className={classes.requestsCount}>
                                    {friendRequests.length}
                                </Typography>
                            </div>

                            <div className={classes.requestListContainer}>
                                {visibleRequests.map((request) => (
                                    <RequestRow 
                                        key={request._id} 
                                        request={request} 
                                        handleAcceptFriendRequest={handleAcceptFriendRequest} 
                                        handleDeclineFriendRequest={handleDeclineFriendRequest} 
                                        friendRequestToCancel={handleFriendRequestToCancel}
                                        avatarCacheBust={catalog?.avatarCacheBust || 0}
                                    />
                                ))}
                            </div>

                            {hasMoreRequests && (
                                <Button
                                    className={classes.showMoreRequestsButton}
                                    onClick={() => setVisibleRequestCount((prev) => prev + 3)}
                                >
                                    Show more requests
                                </Button>
                            )}
                        </div>
                    )}

                    {/* If there are no friends */}
                    {friends.length === 0 && (
                        <Typography variant="body2" style={{ color: '#cccccc', textAlign: 'center', marginTop: '0.75rem', fontSize: '0.82rem' }}>
                            Your friends will appear here
                        </Typography>
                    )}

                    {/* If search yields no results */}
                    {friends.length > 0 && filteredFriends.length === 0 && (
                        <Typography variant="body2" style={{ color: '#cccccc', textAlign: 'center', marginTop: '0.75rem', fontSize: '0.82rem' }}>
                            No friends found matching "{friendSearchQuery}"
                        </Typography>
                    )}

                    <div className={classes.friendListContainer}>
                        {onlineFriends.length > 0 && (
                            <div className={classes.containerOnlineFriends}>
                                <div className={classes.textOnlineFriends}>
                                    ONLINE - {onlineFriends.length}
                                </div>
                                <div className={classes.containerFriends}>
                                    {onlineFriendsInYourTeamSorted.map((friend, index) => {
                                        const isInMyLobby = catalog.currentLobby?.members?.some(m => m.userId === friend.friendData?.userId) || false;
                                        return (
                                            <FriendRow 
                                                key={`${friend.friendSteamId || friend.friendData?._id}-${index}`} 
                                                friend={friend}
                                                handleDeleteFriend={handleDeleteFriend}
                                                sendInvite={handleSendInvite}
                                                inviteState={inviteState[friend.friendData?.userId] || {}}
                                                handleClearInviteError={handleClearInviteError}
                                                lobbyState={catalog.lobbyState}
                                                showUnavailableActionMessage={actions.showUnavailableActionMessage}
                                                currentLobbyCode={catalog.currentLobbyCode}
                                                isInMyLobby={isInMyLobby}
                                                isLobbyFull={isLobbyFull}
                                                isLeader={isLeader}
                                                avatarCacheBust={catalog?.avatarCacheBust || 0}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {offlineFriends.length > 0 && (
                            <div className={classes.containerOfflineFriends}>
                                <div className={classes.textOfflineFriends}>
                                    OFFLINE - {offlineFriends.length}
                                </div>
                                <div className={classes.containerFriends}>
                                    {offlineFriends.map((friend, index) => (
                                        <FriendRow 
                                            key={`${friend.friendSteamId || friend.friendData?._id}-${index}`} 
                                            friend={friend}
                                            handleDeleteFriend={handleDeleteFriend}
                                            sendInvite={handleSendInvite}
                                            avatarCacheBust={catalog?.avatarCacheBust || 0}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}